import type { ReactNode } from 'react';
import { Logo } from './Logo.js';
import '../../pages/auth-page.css';

// Where the marketing site lives — the "Back to website" button points there.
const PUBLIC_URL =
  (import.meta.env.VITE_PUBLIC_URL as string | undefined) ??
  'http://localhost:4040';

/**
 * Split auth layout for sign-in / forgot / reset — the same design the admin
 * console uses (apps/admin/src/components/layout/AuthShell.tsx), so both
 * portals read as one product.
 *
 * Deliberately a separate component from AuthShell: that one is still used by
 * the onboarding wizard, which wants the centred dot-grid card, not this.
 */
export function AuthSplitShell({
  children,
  eyebrow = 'Welcome back',
  headline,
  description,
}: {
  children: ReactNode;
  eyebrow?: string;
  headline?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-label="Proptree manager">
        <div className="auth-field" aria-hidden="true" />
        <div className="auth-orb auth-orb-light" aria-hidden="true" />
        <div className="auth-orb auth-orb-deep" aria-hidden="true" />
        <div className="auth-bloom" aria-hidden="true" />
        <div className="auth-grain" aria-hidden="true" />

        <div className="auth-brand">
          <Logo size="md" />
        </div>

        <div className="auth-panel-copy">
          <p className="auth-eyebrow">{eyebrow}</p>
          <h2>
            {headline ?? (
              <>
                Every listing.
                <br />
                <em>One dashboard.</em>
              </>
            )}
          </h2>
          <p className="auth-panel-description">
            {description ?? (
              <>
                <strong>Listings, leads and your proptree</strong> — everything
                your brokerage runs on, in one place.
              </>
            )}
          </p>
        </div>
      </section>

      <section className="auth-form-column">
        <a className="auth-back" href={PUBLIC_URL}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M19 12H5m7-7-7 7 7 7" />
          </svg>
          Back to website
        </a>

        {children}

        <nav className="auth-legal" aria-label="Legal and support">
          <a href="/privacy">Privacy &amp; terms</a>
          <a href="/contact">Contact us</a>
          <a href="/support">Support</a>
        </nav>
      </section>
    </main>
  );
}

export function AlertIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.9" />
      <path d="M12 7.5V13M12 16.2v.1" stroke="currentColor" strokeWidth="1.9" />
    </svg>
  );
}

export function ArrowIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.9" />
      <path d="m8.5 12.2 2.4 2.4 4.6-4.9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
